import _ from "lodash";
import { ReactNode, createContext, useReducer } from "react";
import { ActionMap } from "types/auth.types";

export type WizardItem = {
  wizardName: string;
  skipStepIndexes?: Array<number>;
  data?: any;
  disableSubmit?: boolean;
  outcome?: any;
};

type WizardState = {
  wizards: WizardItem[];
};

enum ACTION_TYPES {
  START_WIZARD = "START_WIZARD",
  UPDATE_WIZARD = "UPDATE_WIZARD",
  DISCARD_WIZARD = "DISCARD_WIZARD",
  REMOVE_SKIP_STEP = "REMOVE_SKIP_STEP",
  ADD_SKIP_STEP = "ADD_SKIP_STEP",
  CLEAR_ALL_SKIP_STEPS = "CLEAR_ALL_SKIP_STEPS",
  UPDATE_DISABLE_SUBMIT = "UPDATE_DISABLE_SUBMIT",
  ADD_OUTCOME = "ADD_OUTCOME",
}

type WizardPayLoad = {
  [ACTION_TYPES.START_WIZARD]: {
    wizardName: string;
  };
  [ACTION_TYPES.UPDATE_WIZARD]: {
    wizardName: string;
    data: any;
  };
  [ACTION_TYPES.DISCARD_WIZARD]: {
    wizardName: string;
  };
  [ACTION_TYPES.ADD_SKIP_STEP]: {
    wizardName: string;
    skipStepIndex: number;
  };
  [ACTION_TYPES.REMOVE_SKIP_STEP]: {
    wizardName: string;
    skipStepIndex: number;
  };
  [ACTION_TYPES.CLEAR_ALL_SKIP_STEPS]: {
    wizardName: string;
  };
  [ACTION_TYPES.UPDATE_DISABLE_SUBMIT]: {
    wizardName: string;
    disableSubmit: boolean;
  };
  [ACTION_TYPES.ADD_OUTCOME]: {
    wizardName: string;
    outcome: any;
  };
};

export type WizardActions = ActionMap<WizardPayLoad>[keyof ActionMap<WizardPayLoad>];

const WizardReducer = (state: WizardState, action: WizardActions) => {
  const wizard = state.wizards.find((w) => w.wizardName === action.payload.wizardName);
  switch (action.type) {
    case ACTION_TYPES.START_WIZARD:
      if (state.wizards.find((w) => w.wizardName === action.payload.wizardName)) {
        return state;
      }

      return {
        wizards: [
          ...(state.wizards ?? []),
          {
            wizardName: action.payload.wizardName,
            data: {},
            skipStepIndexes: [],
            disableSubmit: false,
            outcome: []
          } as WizardItem,
        ],
      };
    case ACTION_TYPES.UPDATE_WIZARD:
      const existingData = _.omit(wizard?.data, _.keys(action.payload.data));
      return {
        wizards: [
          ...state.wizards?.filter((r) => r.wizardName !== action.payload.wizardName),
          {
            wizardName: action.payload.wizardName,
            data: _.merge(existingData, action.payload.data),
            skipStepIndexes: wizard?.skipStepIndexes ?? [],
            disableSubmit: wizard?.disableSubmit,
            outcome: wizard?.outcome,
          },
        ],
      };
    case ACTION_TYPES.DISCARD_WIZARD:
      return {
        wizards: state.wizards?.filter((r) => r.wizardName !== action.payload.wizardName),
      };
    case ACTION_TYPES.ADD_SKIP_STEP:
      const existingStepsToSkip =
        wizard?.skipStepIndexes?.filter((i) => i !== action.payload.skipStepIndex) ?? [];

      return {
        wizards: [
          ...state.wizards?.filter((r) => r.wizardName !== action.payload.wizardName),
          {
            wizardName: action.payload.wizardName,
            data: wizard?.data ?? {},
            skipStepIndexes: [...existingStepsToSkip, action.payload.skipStepIndex],
            disableSubmit: wizard?.disableSubmit,
            outcome: wizard?.outcome,
          },
        ],
      };
    case ACTION_TYPES.REMOVE_SKIP_STEP:
      return {
        wizards: [
          ...state.wizards?.filter((r) => r.wizardName !== action.payload.wizardName),
          {
            wizardName: action.payload.wizardName,
            data: wizard?.data ?? {},
            skipStepIndexes: wizard?.skipStepIndexes?.filter(
              (r) => r !== action.payload.skipStepIndex
            ),
            disableSubmit: wizard?.disableSubmit,
            outcome: wizard?.outcome,
          },
        ],
      };

    case ACTION_TYPES.CLEAR_ALL_SKIP_STEPS:
      return {
        wizards: [
          ...state.wizards?.filter((r) => r.wizardName !== action.payload.wizardName),
          {
            wizardName: action.payload.wizardName,
            data: wizard?.data ?? {},
            skipStepIndexes: [],
            disableSubmit: wizard?.disableSubmit,
            outcome: wizard?.outcome,
          },
        ],
      };

    case ACTION_TYPES.UPDATE_DISABLE_SUBMIT:
      return {
        wizards: [
          ...state.wizards?.filter((r) => r.wizardName !== action.payload.wizardName),
          {
            wizardName: action.payload.wizardName,
            data: wizard?.data ?? {},
            skipStepIndexes: wizard?.skipStepIndexes ?? [],
            disableSubmit: action.payload.disableSubmit,
            outcome: wizard?.outcome,
          },
        ],
      };

    case ACTION_TYPES.ADD_OUTCOME:
      return {
        wizards: [
          ...state.wizards?.filter((r) => r.wizardName !== action.payload.wizardName),
          {
            wizardName: action.payload.wizardName,
            data: wizard?.data ?? {},
            skipStepIndexes: wizard?.skipStepIndexes ?? [],
            disableSubmit: wizard?.disableSubmit,
            outcome: action.payload.outcome,
          },
        ],
      };

    default:
      return state;
  }
};

const initialState: WizardState = {
  wizards: [],
};

type WizardContextType = {
  startWizard: (wizardName: string) => void;
  discardWizard: (wizardName: string) => void;
  updateWizard: (wizardName: string, data: any) => void;
  addStepToSkip: (wizardName: string, stepToSkip: number) => void;
  removeStepToSkip: (wizardName: string, stepToSkip: number) => void;
  clearAllStepsToSkip: (wizardName: string) => void;
  getWizard: (wizardName: string) => WizardItem | undefined;
  disableSubmit: (wizardName: string, disableSubmit: boolean) => void;
  addOutcome: (wizardName: string, outcome: any) => void;
};

const WizardContext = createContext<WizardContextType | null>(null);

type WizardProviderProps = {
  children: ReactNode;
};

function WizardContextProvider({ children }: WizardProviderProps) {
  const [state, dispatch] = useReducer(WizardReducer, initialState);

  const startWizard = (wizardName: string): void => {
    if (state.wizards.find((w: WizardItem) => w.wizardName === wizardName)) {
      return;
    }

    dispatch({
      type: ACTION_TYPES.START_WIZARD,
      payload: {
        wizardName: wizardName,
      },
    });
  };

  const updateWizard = (wizardName: string, data: any): void => {
    dispatch({
      type: ACTION_TYPES.UPDATE_WIZARD,
      payload: {
        wizardName: wizardName,
        data: data,
      },
    });
  };

  const discardWizard = (wizardName: string): void => {
    dispatch({
      type: ACTION_TYPES.DISCARD_WIZARD,
      payload: {
        wizardName: wizardName,
      },
    });
  };

  const addStepToSkip = (wizardName: string, stepToSkip: number): void => {
    dispatch({
      type: ACTION_TYPES.ADD_SKIP_STEP,
      payload: {
        wizardName: wizardName,
        skipStepIndex: stepToSkip,
      },
    });
  };

  const removeStepToSkip = (wizardName: string, stepToSkip: number): void => {
    dispatch({
      type: ACTION_TYPES.REMOVE_SKIP_STEP,
      payload: {
        wizardName: wizardName,
        skipStepIndex: stepToSkip,
      },
    });
  };

  const clearAllStepsToSkip = (wizardName: string): void => {
    dispatch({
      type: ACTION_TYPES.CLEAR_ALL_SKIP_STEPS,
      payload: {
        wizardName: wizardName,
      },
    });
  };

  const getWizard = (wizardName: string): WizardItem | undefined => {
    const wizard = state.wizards.find((w: WizardItem) => w.wizardName === wizardName);
    if (!wizard) {
      return;
    }

    return wizard;
  };

  const disableSubmit = (wizardName: string, disableSubmit: boolean): void => {
    dispatch({
      type: ACTION_TYPES.UPDATE_DISABLE_SUBMIT,
      payload: {
        wizardName: wizardName,
        disableSubmit: disableSubmit,
      },
    });
  };

  const addOutcome = (wizardName: string, outcome: any): void => {
    dispatch({
      type: ACTION_TYPES.ADD_OUTCOME,
      payload: {
        wizardName: wizardName,
        outcome: outcome,
      },
    });
  };

  return (
    <WizardContext.Provider
      value={{
        startWizard,
        updateWizard,
        discardWizard,
        addStepToSkip,
        removeStepToSkip,
        clearAllStepsToSkip,
        getWizard,
        disableSubmit,
        addOutcome,
      }}
    >
      {children}
    </WizardContext.Provider>
  );
}

export { WizardContext, WizardContextProvider };
